import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  TimePoint,
  TimePointInput,
  TimeWindow,
  TimeWindowInput,
} from '@pipyar/core';
import { api } from '@/lib/api';
import type { InstrumentDto } from '@/lib/useSymbol';

/**
 * Every settings mutation invalidates the market queries as well as its own list.
 *
 * A changed window or pair changes what the charts draw, and without this the
 * Time Windows page would keep showing the previous configuration until its own
 * refetch interval came round — which reads as the save having failed.
 */
function useSettingsMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['settings'] }),
        queryClient.invalidateQueries({ queryKey: ['time-windows'] }),
        queryClient.invalidateQueries({ queryKey: ['instruments'] }),
      ]);
    },
  });
}

// ---------------------------------------------------------------- windows

export function useTimeWindows() {
  return useQuery({
    queryKey: ['settings', 'time-windows'],
    queryFn: () => api.get<{ windows: TimeWindow[] }>('/api/settings/time-windows'),
    staleTime: 30_000,
  });
}

export function useCreateWindow() {
  return useSettingsMutation((input: TimeWindowInput) =>
    api.post<{ window: TimeWindow }>('/api/settings/time-windows', input),
  );
}

export function useUpdateWindow() {
  return useSettingsMutation(({ id, input }: { id: number; input: TimeWindowInput }) =>
    api.put<{ window: TimeWindow }>(`/api/settings/time-windows/${id}`, input),
  );
}

export function useDeleteWindow() {
  return useSettingsMutation((id: number) =>
    api.del<{ ok: true }>(`/api/settings/time-windows/${id}`),
  );
}

// ---------------------------------------------------------------- points

export function useTimePoints() {
  return useQuery({
    queryKey: ['settings', 'time-points'],
    queryFn: () => api.get<{ points: TimePoint[] }>('/api/settings/time-points'),
    staleTime: 30_000,
  });
}

export function useCreatePoint() {
  return useSettingsMutation((input: TimePointInput) =>
    api.post<{ point: TimePoint }>('/api/settings/time-points', input),
  );
}

export function useUpdatePoint() {
  return useSettingsMutation(({ id, input }: { id: number; input: TimePointInput }) =>
    api.put<{ point: TimePoint }>(`/api/settings/time-points/${id}`, input),
  );
}

export function useDeletePoint() {
  return useSettingsMutation((id: number) =>
    api.del<{ ok: true }>(`/api/settings/time-points/${id}`),
  );
}

// ---------------------------------------------------------------- pairs

export function useSettingsInstruments() {
  return useQuery({
    queryKey: ['settings', 'instruments'],
    queryFn: () =>
      api.get<{ instruments: InstrumentDto[]; hint: string }>('/api/settings/instruments'),
    staleTime: 30_000,
  });
}

export function useUpdateInstrument() {
  return useSettingsMutation(
    ({ id, ...body }: { id: number; enabled?: boolean; description?: string }) =>
      api.patch<{ instrument: InstrumentDto }>(`/api/settings/instruments/${id}`, body),
  );
}
